// components/UploadInvoiceDialog.js
import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Fade,
  Alert,
} from "@mui/material";
import { uploadBytes, ref, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../firebase";
import { addDoc, collection, Timestamp } from "firebase/firestore";
import extractInvoiceData from "../utils/extractInvoiceData";

export default function UploadInvoiceDialog({ open, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  // Optional fields for review modal can be added later (like total, VAT, etc.)

  const handleFile = (file) => {
    setFile(file);
    setError("");
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setPreviewUrl(reader.result);
      reader.onerror = () => setPreviewUrl(null);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError("");
    setProgress(0);

    try {
      const user = auth.currentUser;
      const storageRef = ref(
        storage,
        `invoices/uploads/${user.uid}/${file.name}`
      );

      // Animate progress
      let interval = setInterval(() => {
        setProgress((old) => (old < 95 ? old + 5 : old));
      }, 120);

      await uploadBytes(storageRef, file);
      setProgress(100);
      clearInterval(interval);

      const url = await getDownloadURL(storageRef);
      const invoiceData = await extractInvoiceData(file);

      await addDoc(collection(db, "invoices"), {
        userId: user.uid,
        invoiceNumber: invoiceData.invoiceNumber || "Uploaded",
        invoiceDate: invoiceData.invoiceDate
          ? Timestamp.fromDate(new Date(invoiceData.invoiceDate))
          : Timestamp.now(),
        totalAmount: invoiceData.total || 0,
        clientEmail: invoiceData.clientEmail || "",
        clientName: "", // Optionally add OCR name parsing later
        items: [
          {
            description: "Imported invoice total",
            quantity: 1,
            unitPrice: invoiceData.total || 0,
            vat: invoiceData.vat || 0,
          },
        ],
        status: "unpaid",
        uploadedUrl: url,
        createdAt: Timestamp.now(),
        source: "upload",
      });

      setProgress(100);
      setTimeout(() => {
        setFile(null);
        setPreviewUrl(null);
        setProgress(0);
        onUploaded();
        onClose();
      }, 600);
    } catch (err) {
      setError("Upload failed. Try again.");
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>📤 Upload Invoice</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Drag & drop, or click to select an invoice file (image or PDF).
        </Typography>

        <Box
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files.length > 0) {
              handleFile(e.dataTransfer.files[0]);
            }
          }}
          sx={{
            p: 3,
            border: dragOver ? "2px dashed #3b82f6" : "2px dashed #cbd5e1",
            borderRadius: 2,
            textAlign: "center",
            cursor: "pointer",
            mb: 2,
            backgroundColor: dragOver ? "#f0f9ff" : "#f9fafb",
            transition: "all 0.2s",
            "&:hover": {
              backgroundColor: "#f0f9ff",
              borderColor: "#3b82f6",
            },
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => handleFile(e.target.files[0])}
            hidden
          />
          <Typography>
            {file ? file.name : "Click or drag a file to upload"}
          </Typography>
        </Box>

        {previewUrl && (
          <Box sx={{ mb: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Preview:
            </Typography>
            <img
              src={previewUrl}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }}
            />
          </Box>
        )}

        <Fade in={loading || progress > 0}>
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {loading
                ? progress < 100
                  ? `Uploading... ${progress}%`
                  : "Finalizing..."
                : ""}
            </Typography>
            <Box
              sx={{
                height: 8,
                width: "100%",
                backgroundColor: "#e5e7eb",
                borderRadius: 4,
                overflow: "hidden",
                position: "relative",
                mt: 0.5,
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${progress}%`,
                  backgroundColor: progress === 100 ? "#10b981" : "#3b82f6",
                  transition: "width 0.4s, background-color 0.4s",
                }}
              />
            </Box>
          </Box>
        </Fade>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={loading || !file}
        >
          {loading ? <CircularProgress size={20} /> : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
